import { html, render, nothing } from 'lit-html';
import { formatMoney } from 'b2b-plus/ui';
import { paletteColorFor } from 'b2b-plus/utils';

/**
 * Same normalisation as DTC `updateMedia` in global.js (`handleize` in main-product.js).
 * "Black / Black" → "alt--black-black"
 * @param {unknown} value
 * @returns {string}
 */
function altClassForOptionValue(value) {
  return 'alt--' + String(value ?? '')
    .toLowerCase()
    .trim()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Refresh desktop thumbnail height + any already-initialised Swiper after a filter pass.
 * Safe when the gallery or Swiper is missing (desktop, or Swiper not ready yet).
 * @param {Element} productOuter
 */
function refreshProductGallery(productOuter) {
  const mediaList = productOuter.querySelector('.product__media-list.swiper-initialized');
  if (mediaList?.swiper) {
    mediaList.swiper.update();
    if (typeof mediaList.swiper.slideTo === 'function') {
      mediaList.swiper.slideTo(0, 0);
    }
  }

  const mediaSublist = productOuter.querySelector('.product__media-sublist.swiper-initialized');
  if (mediaSublist?.swiper) {
    mediaSublist.swiper.update();
    if (typeof mediaSublist.swiper.slideTo === 'function') {
      mediaSublist.swiper.slideTo(0, 0);
    }
  }

  if (typeof window.productThumbnailHeight === 'function') {
    window.productThumbnailHeight(productOuter);
  }
}

/**
 * Show only `.product__media-item` images tagged for the selected colour.
 * Leaves unclassified `.alt--` items visible (same as DTC `updateMedia`).
 * No-ops when the gallery is missing or the colour has no matching images.
 * @param {unknown} value
 * @param {{ retrySwiper?: boolean }} [opts]
 */
function filterProductMediaByOption(value, { retrySwiper = false } = {}) {
  if (!value) return;

  const productOuter = document.querySelector('.product__outer');
  if (!productOuter) return;

  const altClass = altClassForOptionValue(value);
  if (altClass === 'alt--') return;

  const matching = productOuter.querySelectorAll(`.product__main .product__media-item.${altClass}`);
  if (!matching.length) return;

  productOuter.querySelectorAll(`.product__main .product__media-item:not(.${altClass}):not(.hide-item):not(.alt--)`).forEach((ele) => {
    ele.classList.add('hidden');
    ele.classList.add('hide-item');
  });
  matching.forEach((ele) => {
    ele.classList.remove('hidden');
    ele.classList.remove('hide-item');
  });

  // Thumbnail strip uses the same alt-- classes when gallery_type is thumbs_aside.
  productOuter.querySelectorAll(`.product__main .product__media-subitem:not(.${altClass}):not(.hide-item):not(.alt--)`).forEach((ele) => {
    ele.classList.add('hidden');
    ele.classList.add('hide-item');
  });
  productOuter.querySelectorAll(`.product__main .product__media-subitem.${altClass}`).forEach((ele) => {
    ele.classList.remove('hidden');
    ele.classList.remove('hide-item');
  });

  const totalSlide = productOuter.querySelector('.product__pagination-count .total_slide');
  if (totalSlide) totalSlide.textContent = String(matching.length);

  const currentSlide = productOuter.querySelector('.product__pagination-count .current_slide');
  if (currentSlide) currentSlide.textContent = '1';

  refreshProductGallery(productOuter);

  // Module can connect before update-slider.js initialises Swiper on mobile.
  if (retrySwiper && !productOuter.querySelector('.product__media-list.swiper-initialized')) {
    window.addEventListener('load', () => filterProductMediaByOption(value), { once: true });
  }
}

document.addEventListener('b2b:option-selected', (e) => {
  const value = e.detail?.value;
  if (value) filterProductMediaByOption(value);
});

class B2BPlusOptionSelector extends HTMLElement {
  static observedAttributes = [
    'options',
    'selected',
    'disabled-options',
    'hidden-options',
    'option-images',
    'option-badges',
    'option-colors',
    'option-prices',
    'option-name',
  ];

  #selectedOption = null;
  #optionValues = [];
  // Sold out — inventory records exist but none is orderable. Rendered struck through.
  #disabledOptions = new Set();
  // Not carried — no inventory record at all, so never orderable. Not rendered.
  #hiddenOptions = new Set();
  #optionImages = new Map();
  /** @type {Map<string, string>} option value → inventory_status badge text */
  #optionBadges = new Map();
  /**
   * @type {Map<string, string>} option value → merchant-configured Shopify swatch
   * colour. Beats the shared `config.swatchColors` palette; both lose to a variant image.
   */
  #optionColors = new Map();
  /** @type {Map<string, { price: number, compare_at_price: number|null }>} */
  #optionPrices = new Map();
  /** @type {string} Buyer-facing option name (e.g. Color, Style) */
  #optionName = 'Color';

  get #productHandle() {
    return this.getAttribute('product-handle') ?? '';
  }

  #resolveOptionName(raw) {
    const fromAttr = String(raw ?? '').trim();
    if (fromAttr) return fromAttr;
    const fromConfig = String(window.b2bPlus?.config?.productTableSplitOption ?? '').trim();
    return fromConfig || 'Color';
  }

  #parseOptionValues(raw) {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
    } catch {
      // fall through
    }
    return raw.split(',').map((c) => c.trim()).filter(Boolean);
  }

  #parseOptionImages(raw) {
    const map = new Map();
    if (!raw) return map;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        for (const [k, v] of Object.entries(parsed)) {
          if (v) map.set(String(k), String(v));
        }
      }
    } catch {
      // fall through — no images, letter avatars are used
    }
    return map;
  }

  /**
   * Parse `{ "Color": "New", … }` from Liquid — only non-empty inventory_status values.
   * @param {string|null} raw
   * @returns {Map<string, string>}
   */
  #parseOptionBadges(raw) {
    const map = new Map();
    if (!raw) return map;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        for (const [k, v] of Object.entries(parsed)) {
          const status = String(v ?? '').trim();
          if (status) map.set(String(k), status);
        }
      }
    } catch {
      // fall through — no badges
    }
    return map;
  }

  /**
   * Parse `{ "Navy": { "price": 4500, "compare_at_price": 6000 }, … }` from Liquid.
   * @param {string|null} raw
   * @returns {Map<string, { price: number, compare_at_price: number|null }>}
   */
  #parseOptionPrices(raw) {
    const map = new Map();
    if (!raw) return map;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        for (const [k, v] of Object.entries(parsed)) {
          if (!v || typeof v !== 'object') continue;
          const price = Number(v.price);
          if (!Number.isFinite(price)) continue;
          const compareRaw = v.compare_at_price;
          const compareAt = compareRaw == null || compareRaw === ''
            ? null
            : Number(compareRaw);
          map.set(String(k), {
            price,
            compare_at_price: Number.isFinite(compareAt) ? compareAt : null,
          });
        }
      }
    } catch {
      // fall through — no prices
    }
    return map;
  }

  connectedCallback() {
    this.#optionValues = this.#parseOptionValues(this.getAttribute('options'));
    this.#disabledOptions = new Set(this.#parseOptionValues(this.getAttribute('disabled-options')));
    this.#hiddenOptions = new Set(this.#parseOptionValues(this.getAttribute('hidden-options')));
    this.#optionImages = this.#parseOptionImages(this.getAttribute('option-images'));
    this.#optionBadges = this.#parseOptionBadges(this.getAttribute('option-badges'));
    this.#optionColors = this.#parseOptionImages(this.getAttribute('option-colors'));
    this.#optionPrices = this.#parseOptionPrices(this.getAttribute('option-prices'));
    this.#optionName = this.#resolveOptionName(this.getAttribute('option-name'));
    const sel = this.getAttribute('selected');
    this.#selectedOption = (sel && this.#optionValues.includes(sel)) ? sel : (this.#optionValues[0] ?? null);
    this.#render();
    filterProductMediaByOption(this.#selectedOption, { retrySwiper: true });
    this.addEventListener('click', this.#onClick);
    // Keep in sync when the product-table option tabs (or another selector) change.
    document.addEventListener('b2b:option-selected', this.#onExternalOptionSelected);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('b2b:option-selected', this.#onExternalOptionSelected);
  }

  attributeChangedCallback(name, _old, val) {
    if (name === 'options') {
      this.#optionValues = this.#parseOptionValues(val);
      if (!this.#optionValues.includes(this.#selectedOption)) {
        this.#selectedOption = this.#optionValues[0] ?? null;
      }
      this.#render();
    } else if (name === 'selected') {
      if (val && val !== this.#selectedOption && this.#optionValues.includes(val)) {
        this.#selectedOption = val;
        this.#render();
      }
    } else if (name === 'disabled-options') {
      this.#disabledOptions = new Set(this.#parseOptionValues(val));
      this.#render();
    } else if (name === 'hidden-options') {
      this.#hiddenOptions = new Set(this.#parseOptionValues(val));
      this.#render();
      // Inventory just arrived: Liquid may have selected a colour the app does
      // not carry (omitted from the strip). Activate the first visible swatch.
      this.#clickFirstVisibleSwatchIfNeeded();
    } else if (name === 'option-images') {
      this.#optionImages = this.#parseOptionImages(val);
      this.#render();
    } else if (name === 'option-badges') {
      this.#optionBadges = this.#parseOptionBadges(val);
      this.#render();
    } else if (name === 'option-colors') {
      this.#optionColors = this.#parseOptionImages(val);
      this.#render();
    } else if (name === 'option-prices') {
      this.#optionPrices = this.#parseOptionPrices(val);
    } else if (name === 'option-name') {
      this.#optionName = this.#resolveOptionName(val);
      this.#render();
    }
  }

  #onClick = (e) => {
    const btn = e.target.closest('[data-b2b-option]');
    if (!btn) return;
    const value = btn.dataset.b2bOption;
    if (!value || value === this.#selectedOption
      || this.#disabledOptions.has(value) || this.#hiddenOptions.has(value)) return;
    this.#applySelected(value, { dispatch: true });
  };

  /**
   * After inventory marks some colours as not-carried, the Liquid-selected
   * value (e.g. first available variant) may no longer have a swatch. Click
   * the first remaining tab so the label, table, and gallery all follow.
   */
  #clickFirstVisibleSwatchIfNeeded() {
    if (this.#selectedOption && !this.#hiddenOptions.has(this.#selectedOption)) return;
    const btn =
      this.querySelector('.b2b-plus-option-tabs .b2b-plus-option-tab:not([disabled])')
      ?? this.querySelector('.b2b-plus-option-tabs .b2b-plus-option-tab');
    btn?.click();
  }

  /**
   * Mirror product-table (or peer selector) option changes onto this strip.
   * Same-handle only; no re-dispatch — avoids a feedback loop.
   * @param {CustomEvent} e
   */
  #onExternalOptionSelected = (e) => {
    const { value, productHandle } = e.detail ?? {};
    if (!value || productHandle !== this.#productHandle) return;
    if (value === this.#selectedOption) return;
    if (!this.#optionValues.includes(value) || this.#disabledOptions.has(value)) return;
    if (this.#hiddenOptions.has(value)) return;
    this.#applySelected(value, { dispatch: false });
  };

  /**
   * @param {string} value
   * @param {{ dispatch?: boolean }} [opts]
   */
  #applySelected(value, { dispatch = false } = {}) {
    this.#selectedOption = value;
    this.setAttribute('selected', value);
    this.#render();
    this.#updateProductPrice(value);
    if (!dispatch) return;
    document.dispatchEvent(
      new CustomEvent('b2b:option-selected', {
        bubbles: true,
        detail: { value, productHandle: this.#productHandle },
      }),
    );
  }

  /**
   * Refresh theme PDP wholesale + MSRP for the selected option value.
   * @param {string} value
   */
  #updateProductPrice(value) {
    const pricing = this.#optionPrices.get(value);
    if (!pricing) return;

    const info =
      this.closest('.product__info-container')
      ?? this.parentElement;
    if (!info) return;

    let priceWrapper = info.querySelector('.price-wrapper');
    if (!priceWrapper && this.previousElementSibling?.classList?.contains('price-wrapper')) {
      priceWrapper = this.previousElementSibling;
    }
    if (!priceWrapper) priceWrapper = info.querySelector('[id^="price-"]');

    const money = formatMoney(pricing.price);
    const regularItem =
      priceWrapper?.querySelector('.price__regular .price-item--regular')
      ?? priceWrapper?.querySelector('.price-item--regular');
    if (regularItem) {
      const label = regularItem.querySelector('.price__label');
      // Keep the wholesale label node; replace only the money text.
      regularItem.textContent = '';
      if (label) regularItem.appendChild(label);
      regularItem.appendChild(document.createTextNode(` ${money}`));
    }
    const saleItem = priceWrapper?.querySelector('.price-item--sale');
    if (saleItem) saleItem.textContent = money;

    const msrp = info.querySelector('.MSRP-price');
    if (!msrp) return;

    const compareAt = pricing.compare_at_price;
    const showMsrp = compareAt != null && compareAt > pricing.price;
    if (!showMsrp) {
      msrp.hidden = true;
      msrp.style.display = 'none';
      return;
    }

    msrp.hidden = false;
    msrp.style.display = '';
    const spans = msrp.querySelectorAll('span');
    const amountSpan = spans.length > 1 ? spans[spans.length - 1] : null;
    if (amountSpan) amountSpan.textContent = formatMoney(compareAt);
  }

  #render() {
    if (!this.#optionValues.length) {
      render(nothing, this);
      return;
    }
    const selected = this.#selectedOption;
    render(
      html`
        <div class="b2b-plus-selected-option-ele" style="min-width: 200px;margin-bottom: 12px;">
          <span class="b2b-plus-selected-option-label"><strong>${this.#optionName}:</strong> ${selected}</span>
        </div>
        <div class="b2b-plus-option-tabs" role="tablist">
          ${this.#optionValues.map((v) => {
            // Not carried by the app — omit entirely (mirrors optionTabsTemplate).
            if (this.#hiddenOptions.has(v)) return nothing;
            const outOfStock = this.#disabledOptions.has(v);
            const label = String(v ?? '');
            const imageUrl = this.#optionImages.get(label) ?? '';
            // Variant image → merchant-configured Shopify swatch → shared
            // `config.swatchColors` palette → letter avatar. See
            // `snippets/b2b-plus-swatch-palette.liquid` (single source of truth).
            const color = imageUrl ? '' : (this.#optionColors.get(label) || paletteColorFor(label));
            const firstLetter = label.trim().charAt(0).toUpperCase() || '?';
            let swatch;
            if (imageUrl) {
              swatch = html`<img class="b2b-plus-option-tab__img" src=${imageUrl} alt="" loading="lazy" />`;
            } else if (color) {
              swatch = html`<span
                class="b2b-plus-option-tab__color"
                style=${`background: ${color};`}
                aria-hidden="true"
              ></span>`;
            } else {
              swatch = html`<span class="b2b-plus-option-tab__letter" aria-hidden="true">${firstLetter}</span>`;
            }
            const inventoryStatus = this.#optionBadges.get(label) ?? '';
            return html`
              <button
                role="tab"
                type="button"
                aria-label=${label}
                class="b2b-plus-option-tab${v === selected ? ' b2b-plus-option-tab--active' : ''}"
                data-b2b-option=${v}
                ?data-no-image=${!imageUrl && !color}
                ?data-out-of-stock=${outOfStock}
                ?disabled=${outOfStock}
                ?aria-selected=${v === selected}
              >
                ${swatch}
                <span class="b2b-plus-option-tab__tooltip" role="tooltip">${label}</span>
                ${inventoryStatus
                  ? html`<label class="b2b-plus-option-tab__label" for="b2b-plus-option-tab-${v}">${inventoryStatus}</label>`
                  : nothing}
              </button>
            `;
          })}
        </div>
      `,
      this,
    );
  }
}

customElements.define('b2b-plus-option-selector', B2BPlusOptionSelector);
